import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVendors } from '../../context/VendorContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import {
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  PencilIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const VendorView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { vendors, getVendor, getVendors } = useVendors();
  const { hideLoading } = useLoading();
  const getRoutePath = useRoutePath();

  const [vendor, setVendor] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  const hasFetchedVendor = useRef(false);

  const fetchVendor = useCallback(
    async () => {
      if (!id || hasFetchedVendor.current) return;

      hasFetchedVendor.current = true;
      setIsFetching(true);

      try {
        // Try to get from already loaded context first
        let found = getVendor(id);

        // If not found (e.g. direct URL hit), load all vendors once
        if (!found) {
          const response = await getVendors(1, { all: true });

          if (response?.data) {
            const list = response.data;
            if (Array.isArray(list)) {
              found = list.find((item) => Number(item.id) === Number(id));
            }
          }
        }

        if (found) {
          setVendor(found);
        } else {
          setVendor(null);
        }
      } catch (error) {
        console.error('Error fetching vendor data:', error);
      } finally {
        setIsFetching(false);
        hideLoading();
      }
    },
    [id, getVendor, getVendors, hideLoading]
  );

  useEffect(() => {
    // If list is already in context we might be able to resolve quickly
    if (id && vendors?.data?.length && !vendor) {
      const existing = vendors.data.find(
        (item) => Number(item.id) === Number(id)
      );
      if (existing) {
        setVendor(existing);
        setIsFetching(false);
        return;
      }
    }

    fetchVendor();
  }, [id, vendors, vendor, fetchVendor]);

  const handleEdit = () => {
    navigate(getRoutePath(`/vendors/edit/${id}`));
  };

  const getStatusBadge = () => {
    const isActive = vendor?.status === 1 || vendor?.active;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isActive ? (
          <>
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircleIcon className="h-4 w-4 mr-1" />
            Inactive
          </>
        )}
      </span>
    );
  };

  if (!isFetching && !vendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Vendor Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The vendor you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <button
            onClick={() => navigate(getRoutePath('/vendors'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Vendors
          </button>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading vendor information...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            {/* Left side */}
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <BuildingOffice2Icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {vendor.name || 'Vendor Details'}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>{getStatusBadge()}</span>
                </div>
              </div>
            </div>

            {/* Right side buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/vendors'))}
                className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>

              <button
                onClick={handleEdit}
                className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Vendor
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="xl:col-span-2 space-y-6">
            {/* Vendor Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <BuildingOffice2Icon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Vendor Information
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor Name
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {vendor.name || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {vendor.department || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Title
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {vendor.job_title || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Terms
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {vendor.payment_term_name || vendor.payment_term_id || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Primary Contact & Communication Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Primary Contact
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {`${vendor.first_name || ''} ${
                        vendor.last_name || ''
                      }`.trim() || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prefix / Suffix
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {vendor.prefix_name || vendor.prefix_id || vendor.suffix
                        ? `${vendor.prefix_name || ''} ${vendor.suffix || ''}`.trim()
                        : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    Phone Numbers
                  </label>
                  {(vendor.phone_numbers || []).length > 0 ? (
                    (vendor.phone_numbers || []).map((phone, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-wide text-gray-500">
                            {phone.type || 'Phone'}
                          </span>
                          <div className="text-gray-900">
                            {phone.number || 'N/A'}{' '}
                            {phone.ext && (
                              <span className="text-gray-500">
                                (Ext: {phone.ext})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No phone numbers</p>
                  )}
                </div>

                {/* Emails */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    Email Addresses
                  </label>
                  {(vendor.emails || []).length > 0 ? (
                    (vendor.emails || []).map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-wide text-gray-500">
                            {email.type || 'Email'}
                          </span>
                          <div className="text-gray-900">
                            {email.address || 'N/A'}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No email addresses</p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Notes
                  </h2>
                </div>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Internal / Private Notes
                  </h3>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900 min-h-[60px]">
                    {vendor.private_notes || 'No private notes.'}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Public / Order Notes
                  </h3>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900 min-h-[60px]">
                    {vendor.order_notes || 'No order notes.'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Address & Summary */}
          <div className="space-y-6">
            {/* Address Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 text-gray-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Address
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div>
                  <span className="text-gray-600 block mb-1">
                    Street Address
                  </span>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                    {vendor.street_address || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-600 block mb-1">
                    Apt / Suite / Unit
                  </span>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                    {vendor.apt_suite_unit || 'N/A'}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-600 block mb-1">City</span>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {vendor.city || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600 block mb-1">
                      State / Province
                    </span>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {vendor.state || 'N/A'}
                    </div>
                  </div>
                </div>

                <div>
                  <span className="text-gray-600 block mb-1">
                    ZIP / Postal Code
                  </span>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                    {vendor.zipcode || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">
                  Vendor Summary
                </h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge()}
                </div>

                {vendor.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Created At
                    </div>
                    <div className="text-sm text-gray-900">
                      {vendor.formated_created_at}
                    </div>
                  </div>
                )}

                {vendor.formated_updated_at && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Last Updated
                    </div>
                    <div className="text-sm text-gray-900">
                      {vendor.formated_updated_at}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorView;

